import auth from '@/authfile';
import useAcvDatadog from '@/composables/useAcvDatadog';
import { hasMagicLinkToken } from '@/utilities/authTokens';
import { useFeatureFlagsStore } from '@/stores/useFeatureFlags';
import { featureFlagSlugs } from '@/constants/featureFlagSlugs';

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (to.path === '/logout' || to.path === '/open-in-browser') {
    return;
  }
  const config = useRuntimeConfig();
  const auth0 = await auth(
    config.public.AUTH0_DOMAIN,
    config.public.AUTH0_CLIENT_ID,
    config.public.REDIRECT_URL,
    config.public.AUTH0_AUDIENCE,
  );

  const { addRumError, addRumAction } = useAcvDatadog();
  const isAuthenticated = await auth0.isAuthenticated();

  const featureFlagsStore = useFeatureFlagsStore();
  const isInterstitialFixEnabled = await featureFlagsStore.fetchFlagByName(
    featureFlagSlugs.transportMagicLinkInterstitial,
  );

  // Magic-link sessions never touch the Auth0 SDK, so isAuthenticated() stays false for
  // them; hasMagicLinkToken() is our own signal for "already has a valid magic-link session".
  // Flag off falls straight through to the pre-fix behavior below (loginWithRedirect()).
  const hash = window?.location?.hash;
  const hasMagicLinkHash = isInterstitialFixEnabled && hash?.includes('access_token');
  const hasMagicLinkSession = isInterstitialFixEnabled && hasMagicLinkToken();

  // Missing marker means this isn't the browser that requested the link (e.g. a mail
  // app's in-app browser) - send to an interstitial instead of looping loginWithRedirect().
  if (!isAuthenticated && hasMagicLinkHash) {
    const hasPendingRequestMarker = !!localStorage.getItem('magic_link_pending_id');

    if (!hasPendingRequestMarker) {
      addRumAction('magic_link_opened_in_different_browser', {
        userAgent: navigator?.userAgent,
        path: to.fullPath,
      });

      // navigateTo() drops the hash, so stash the real link first - sessionStorage, not a
      // query param, since a raw '#' in the value would just start a new URL fragment.
      sessionStorage.setItem('magic_link_original_url', window.location.href);
      return navigateTo('/open-in-browser');
    }
  }

  if (!isAuthenticated && !hasMagicLinkHash && !hasMagicLinkSession) {
    localStorage.removeItem('magic_link_token');

    const query = to?.query;

    if (query && query.code && query.state) {
      return await auth0.handleRedirectCallback();
    } else {
      // Do not set cached path for /check-email, /legal and /logout routes
      if (to.fullPath !== '/check-email' && to.fullPath !== '/legal' && to.fullPath !== '/logout') {
        // cache the path so we can redirect after authenticating
        localStorage.setItem('cachedPath', JSON.stringify(to.fullPath));
      }
      await auth0.loginWithRedirect();
      return abortNavigation('Login required');
    }
  }

  if (hasMagicLinkHash) {
    const params = new URLSearchParams(hash.substring(1));
    const magicLinkToken = params.get('access_token');

    if (magicLinkToken) {
      localStorage.setItem('magic_link_token', magicLinkToken);
      localStorage.removeItem('magic_link_pending_id');

      try {
        const payload = JSON.parse(atob(magicLinkToken.split('.')[1]));
        const email = payload['https://acvauctions.com/email'];
        if (email) {
          localStorage.setItem('user_email', email);
        }
      } catch (e) {
        addRumError('Failed to parse user email from magic link token', { error: e });
      }
    }

    return;
  } else if (hash?.includes('error')) {
    // Handle error if needed
    const params = new URLSearchParams(hash.substring(1));
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    console.error('Error during authentication:', error, errorDescription);
  }

  // getTokenSilently() below would fail for a magic-link-only session.
  if (!isAuthenticated && hasMagicLinkSession) {
    return;
  }

  // get token and set in localstorage
  const token = await auth0.getTokenSilently();
  localStorage.setItem('access_token', token);

  // Extract and store user email from Auth0 token
  // This is used during the signup process because user information is not available in our system yet
  const tokenString = config.public.AUTH0_CLIENT_ID;
  let authToken;
  try {
    authToken = JSON.parse(
      localStorage.getItem(
        `@@auth0spajs@@::${tokenString}::transport-load-board::openid profile email`,
      ),
    );
    localStorage.setItem('user_email', authToken.body?.decodedToken?.user?.email);
  } catch (e) {
    addRumError('Failed to parse user email from auth token', {
      error: e,
      authToken,
      tokenString,
    });
  }
});
